<template>
  <div class="availability-alert">
    <AlertCircle :size="26" class="availability-alert__icon" />
    <p class="availability-alert__title">{{ alertContent.title }}</p>
    <p class="availability-alert__text">{{ alertContent.text }}</p>
    <p class="availability-alert__text" v-if="alertContent.detail">{{ alertContent.detail }}</p>
    <button type="button" class="availability-alert__action" v-if="alertContent.action"
      @click="handleAction">{{ alertContent.action.label }}</button>
  </div>
</template>

<script>
  import moment from 'moment';
  import { AlertCircle } from 'lucide-vue';
  import { UNAVAILABLE_REASON } from '@/utils/activityAvailability.js';
  import { formatQtyText } from '@/utils/activityCart.js';

  const ACTION = {
    CHANGE_QTY: 'change-qty',
    NEXT_DAY: 'next-day'
  }

  export default {
    name: 'AvailabilityAlert',
    components: {
      AlertCircle
    },
    props: {
      reason: {
        type: Object,
        default: () => ({})
      },
      serviceDate: {
        type: [Date, String],
        default: ''
      },
      qty: {
        type: Number,
        default: 1
      },
      unit: {
        type: Object,
        default: () => ({})
      },
      isSchedule: {
        type: Boolean,
        default: false
      },
      canTryNextDay: {
        type: Boolean,
        default: true
      },
      errorMessage: {
        type: String,
        default: ''
      }
    },
    computed: {
      dateText() {
        return this.$formatDate(this.serviceDate)
      },
      dayName() {
        return moment(this.serviceDate).format('dddd')
      },
      nextDayAction() {
        return this.canTryNextDay ? { type: ACTION.NEXT_DAY, label: 'Try the next day' } : null
      },
      alertContent() {
        const builders = {
          [UNAVAILABLE_REASON.CLOSED_DAY]: this.buildClosedDay,
          [UNAVAILABLE_REASON.NO_SCHEDULE]: this.buildNoSchedule,
          [UNAVAILABLE_REASON.CUT_OFF_PASSED]: this.buildCutOffPassed,
          [UNAVAILABLE_REASON.SOLD_OUT]: this.buildSoldOut,
          [UNAVAILABLE_REASON.HELD_IN_CART]: this.buildHeldInCart,
          [UNAVAILABLE_REASON.NOT_ENOUGH_QTY]: this.buildNotEnoughQty,
          [UNAVAILABLE_REASON.FILTER_CHANGED]: this.buildFilterChanged
        }
        const builder = builders[this.reason.code] || this.buildRequestFailed
        return builder()
      }
    },
    methods: {
      handleAction() {
        const { action } = this.alertContent
        if (action.type == ACTION.CHANGE_QTY) this.$emit(ACTION.CHANGE_QTY, this.reason.remaining)
        else this.$emit(ACTION.NEXT_DAY)
      },
      buildClosedDay() {
        return {
          title: `Closed on ${this.dayName}s`,
          text: `We don't operate on ${this.dayName}s, so there are no sessions on ${this.dateText}. Please choose another date.`,
          action: this.nextDayAction
        }
      },
      buildNoSchedule() {
        return {
          title: 'No sessions on this date',
          text: `There are no sessions scheduled for ${this.dateText}. Please choose another date.`,
          action: this.nextDayAction
        }
      },
      buildCutOffPassed() {
        const { cutOffHours, lastSessionStart } = this.reason
        const last_session = lastSessionStart
          ? this.$customFormatDateTime(lastSessionStart, 'HH:mm:ss', 'hh:mm A')
          : ''

        return {
          title: 'Booking closed for this date',
          text: cutOffHours > 0
            ? this.buildCutOffText(cutOffHours, last_session)
            : this.buildSessionStartedText(last_session),
          action: this.nextDayAction
        }
      },
      buildCutOffText(cut_off_hours, last_session) {
        const hours_text = `${cut_off_hours} ${this.$pluralWord(cut_off_hours, 'hour')}`
        if (!last_session) return `Sessions must be booked at least ${hours_text} before they start. Booking for ${this.dateText} has closed.`
        return `The last session on ${this.dateText} starts at ${last_session} and must be booked at least ${hours_text} in advance, so booking for this date has closed.`
      },
      buildSessionStartedText(last_session) {
        if (!last_session) return `All sessions on ${this.dateText} have already started.`
        return `All sessions on ${this.dateText} have already started. The last one began at ${last_session}.`
      },
      buildSoldOut() {
        const subject = this.isSchedule ? 'All sessions' : 'This item'
        const verb = this.isSchedule ? 'are' : 'is'
        return {
          title: 'Fully booked',
          text: `${subject} on ${this.dateText} ${verb} fully booked. Please choose another date.`,
          action: this.nextDayAction
        }
      },
      buildHeldInCart() {
        return {
          title: 'Already in your cart',
          text: `The remaining availability for ${this.dateText} is already taken by items in your cart. Check your cart or choose another date.`,
          action: this.nextDayAction
        }
      },
      buildNotEnoughQty() {
        const remaining_text = formatQtyText(this.reason.remaining, this.unit)
        const scope = this.isSchedule ? ' per session' : ''
        return {
          title: `Only ${remaining_text} left`,
          text: `You requested ${formatQtyText(this.qty, this.unit)}, but only ${remaining_text}${scope} ${this.reason.remaining > 1 ? 'are' : 'is'} available on ${this.dateText}. Reduce the quantity or choose another date.`,
          action: { type: ACTION.CHANGE_QTY, label: `Book ${remaining_text} instead` }
        }
      },
      buildFilterChanged() {
        return {
          title: 'Oops!',
          text: 'It looks like you made a change to the quantity or the date. Please click the "Check availability" button to get data that matches your filter.'
        }
      },
      buildRequestFailed() {
        return {
          title: 'Oops!',
          text: 'Sorry! Something went wrong.',
          detail: this.errorMessage ? `Detail Error : ${this.errorMessage}` : ''
        }
      }
    }
  }
</script>

<style scoped>
  .availability-alert {
    margin-top: 22px;
    padding: 20px;
    border-radius: 12px;
    background: #fdf1f1;
    text-align: center;
  }

  .availability-alert__icon {
    color: #d05a5a;
    margin-bottom: 8px;
  }

  .availability-alert__title {
    margin: 0 0 4px;
    font-size: 14px;
    font-weight: 600;
    color: #c04545;
  }

  .availability-alert__text {
    margin: 0;
    font-size: 12px;
    line-height: 1.6;
    color: #8a5c5c;
  }

  .availability-alert__action {
    margin-top: 14px;
    height: 40px;
    padding: 0 18px;
    border: 1px solid var(--merchant-primary-color);
    border-radius: 12px;
    background: #fff;
    color: var(--merchant-primary-color);
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: opacity 0.15s;
  }

  .availability-alert__action:hover {
    opacity: 0.85;
  }
</style>



// WEBPACK FOOTER //
// src/components/v2/activity/AvailabilityAlert.vue